import { useEffect } from "react";
import api from "./api";

/**
 * Loads GA4 / Meta Pixel / TikTok Pixel / Microsoft Clarity **only when the
 * corresponding ID has been configured** in the site-settings admin panel.
 * Also injects Google Search Console and Bing verification meta tags.
 *
 * All loads are async/defer — never render-blocking. Safe to call once from
 * the app root.
 */
export const useAnalyticsAndVerification = () => {
    useEffect(() => {
        let cancelled = false;
        api.get("/seo/site-settings")
            .then((r) => {
                if (cancelled) return;
                const s = r.data || {};
                injectVerification(s);
                if (s.ga4_measurement_id) loadGA4(s.ga4_measurement_id);
                if (s.meta_pixel_id) loadMetaPixel(s.meta_pixel_id);
                if (s.tiktok_pixel_id) loadTikTokPixel(s.tiktok_pixel_id);
                if (s.clarity_project_id) loadClarity(s.clarity_project_id);
                if (s.organization_jsonld) injectJsonLd("seo-org", s.organization_jsonld);
                if (s.website_jsonld) injectJsonLd("seo-website", s.website_jsonld);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);
};

const setMeta = (name, content, attr = "name") => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const injectVerification = (s) => {
    if (s.gsc_verification) setMeta("google-site-verification", s.gsc_verification);
    if (s.bing_verification) setMeta("msvalidate.01", s.bing_verification);
};

const injectJsonLd = (id, data) => {
    if (document.getElementById(id)) return;
    const el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
};

const loadScript = (id, src, attrs = {}) => {
    if (document.getElementById(id)) return;
    const el = document.createElement("script");
    el.id = id;
    el.async = true;
    el.src = src;
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
};

const loadGA4 = (id) => {
    if (window.__ga4Loaded) return;
    window.__ga4Loaded = true;
    loadScript("ga4-src", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", id, { anonymize_ip: true });
};

const loadMetaPixel = (id) => {
    if (window.__metaPixelLoaded) return;
    window.__metaPixelLoaded = true;
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
        t = b.createElement(e); t.async = !0; t.src = v;
        s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", id);
    window.fbq("track", "PageView");
};

const loadTikTokPixel = (id) => {
    if (window.__ttqLoaded) return;
    window.__ttqLoaded = true;
    /* eslint-disable */
    !(function (w, d, t) {
        w.TiktokAnalyticsObject = t;
        var ttq = (w[t] = w[t] || []);
        ttq.methods = "page track identify instances debug on off once ready alias group enableCookie disableCookie".split(" ");
        ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
        for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq.instance = function (t) {
            var e = ttq._i[t] || [];
            for (var n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
            return e;
        };
        ttq.load = function (e, n) {
            var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
            ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r;
            ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
            ttq._o = ttq._o || {}; ttq._o[e] = n || {};
            var o = d.createElement("script"); o.type = "text/javascript"; o.async = !0; o.src = r + "?sdkid=" + e + "&lib=" + t;
            var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a);
        };
        ttq.load(id);
        ttq.page();
    })(window, document, "ttq");
    /* eslint-enable */
};

const loadClarity = (id) => {
    if (window.__clarityLoaded) return;
    window.__clarityLoaded = true;
    /* eslint-disable */
    (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", id);
    /* eslint-enable */
};
